<template>
  <div class="max-w-7xl mx-auto pb-10 relative">
    <div class="space-y-6 transition-all duration-300 animate-fade-in" :class="{ 'blur-sm pointer-events-none opacity-50': showModal }">

      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 class="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">Reversiones de Desembolso</h1>
          <p class="text-slate-500 dark:text-slate-400 text-sm">Solicitudes para anular y revertir créditos desembolsados.</p>
        </div>
        <button @click="openCreate" class="flex items-center gap-2 px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl shadow-lg shadow-red-500/30 transition-all active:scale-95">
          <RotateCcw class="w-5 h-5" /> Nueva Reversión
        </button>
      </div>

      <div class="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col sm:flex-row gap-4">
        <div class="relative flex-1">
          <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input v-model="searchTerm" type="text" placeholder="Buscar por código de crédito o cliente..." class="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all">
        </div>
        <select v-model="statusFilter" class="px-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none min-w-[200px]">
          <option value="">Todos los Estados</option>
          <option value="PENDING">Pendiente Autorización</option>
          <option value="REVERTED">Revertido (Ejecutado)</option>
          <option value="REJECTED">Rechazado</option>
        </select>
      </div>

      <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full text-sm text-left">
            <thead class="text-xs text-slate-500 uppercase bg-slate-50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800">
              <tr>
                <th class="px-6 py-4 font-semibold">Crédito / Cliente</th>
                <th class="px-6 py-4 font-semibold">Motivo</th>
                <th class="px-6 py-4 font-semibold text-right">Monto a Revertir</th>
                <th class="px-6 py-4 font-semibold">Estado</th>
                <th class="px-6 py-4 font-semibold text-right">Acciones</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100 dark:divide-slate-800">
              <tr v-if="isLoading">
                <td colspan="5" class="px-6 py-12 text-center text-slate-500">
                  <Loader2 class="w-8 h-8 animate-spin mx-auto mb-3 text-brand-500" /> Cargando reversiones...
                </td>
              </tr>
              <tr v-else-if="filteredList.length === 0">
                <td colspan="5" class="px-6 py-12 text-center text-slate-500">
                  <RotateCcw class="w-8 h-8 mx-auto mb-3 opacity-20" /> No hay reversiones registradas.
                </td>
              </tr>
              <tr v-else v-for="item in filteredList" :key="item.id" class="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                <td class="px-6 py-4">
                  <div class="font-bold text-slate-900 dark:text-slate-100">{{ item.customer_name }}</div>
                  <div class="text-[10px] font-mono text-slate-500 mt-1 uppercase">EXP: {{ item.loan_code }}</div>
                </td>
                <td class="px-6 py-4">
                  <div class="text-xs text-slate-600 dark:text-slate-400 max-w-xs truncate" :title="item.motive">{{ item.motive }}</div>
                  <div class="text-[10px] text-slate-400 mt-1">Solicitado por: {{ item.requested_by_name }}</div>
                </td>
                <td class="px-6 py-4 text-right">
                  <div class="font-bold text-red-600">-${{ Number(item.amount).toLocaleString('en-US', { minimumFractionDigits: 2 }) }}</div>
                </td>
                <td class="px-6 py-4">
                  <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border" :class="getStatusClasses(item.status)">
                    <component :is="getStatusIcon(item.status)" class="w-3.5 h-3.5" />
                    {{ getStatusLabel(item.status) }}
                  </span>
                </td>
                <td class="px-6 py-4 text-right">
                  <button @click="openEdit(item)" class="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Ver / Autorizar">
                    <FileCheck class="w-5 h-5" />
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <ReversionForm :show="showModal" @close="showModal = false" @saved="loadData" :reversionToEdit="selectedReversion" />
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { Search, RotateCcw, Loader2, Clock, CheckCircle, XCircle, FileCheck } from 'lucide-vue-next';
import apiClient from '@/api/client';
import { SecureStorage } from '@/utils/secureStorage';
import { useToast } from '@/composables/useToast';
import ReversionForm from './Form.vue';

const list = ref([]);
const isLoading = ref(true);
const searchTerm = ref('');
const statusFilter = ref('');
const showModal = ref(false);
const selectedReversion = ref({});
const { showError } = useToast();

const loadData = async () => {
  isLoading.value = true;
  try {
    const user = SecureStorage.getItem('llevo_user');
    if (!user?.company) return;
    const res = await apiClient.get(`/loan_reversions?company_id=${user.company}`);
    list.value = Array.isArray(res.data) ? res.data : (res.data.data || []);
  } catch (e) {
    showError('Error cargando las reversiones.');
  } finally {
    isLoading.value = false;
  }
};

onMounted(loadData);

const filteredList = computed(() => {
  let result = list.value;
  if (statusFilter.value) result = result.filter(i => i.status === statusFilter.value);
  if (searchTerm.value) {
    const term = searchTerm.value.toLowerCase();
    result = result.filter(i => i.customer_name?.toLowerCase().includes(term) || i.loan_code?.toLowerCase().includes(term));
  }
  return result;
});

const getStatusClasses = (status) => {
  const map = {
    'PENDING': 'bg-amber-50 text-amber-700 border-amber-200',
    'REVERTED': 'bg-emerald-50 text-emerald-700 border-emerald-200',
    'REJECTED': 'bg-red-50 text-red-700 border-red-200',
  };
  return map[status] || 'bg-slate-100 text-slate-600 border-slate-200';
};
const getStatusIcon = (status) => {
  const map = { 'PENDING': Clock, 'REVERTED': CheckCircle, 'REJECTED': XCircle };
  return map[status] || Clock;
};
const getStatusLabel = (status) => {
  const map = { 'PENDING': 'Pendiente', 'REVERTED': 'Revertido', 'REJECTED': 'Rechazado' };
  return map[status] || 'Desconocido';
};

const openCreate = () => { selectedReversion.value = {}; showModal.value = true; };
const openEdit = (item) => { selectedReversion.value = { ...item }; showModal.value = true; };
</script>